"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivities, getActivityTypes } from "@/state/activity/activity.slice";
import { fetchUsers } from "@/state/users/users.slice";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ClockCounterClockwise,
  MagnifyingGlass,
  FunnelSimple,
  User,
  ShoppingCart,
  Package,
  Gear,
  SignIn,
  SignOut,
  ArrowsClockwise
} from "@phosphor-icons/react";
import { ChevronLeft, ChevronRight, Loader2, Calendar } from "lucide-react";

// Activity type icons and colors
const getActivityTypeConfig = (type) => {
  const configs = {
    auth: { icon: SignIn, color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400", label: "Authentication" },
    user: { icon: User, color: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400", label: "User" },
    product: { icon: Package, color: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400", label: "Product" },
    sale: { icon: ShoppingCart, color: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400", label: "Sale" },
    stock: { icon: ArrowsClockwise, color: "bg-cyan-500", label: "Stock" },
    customer: { icon: User, color: "bg-pink-500", label: "Customer" },
    supplier: { icon: User, color: "bg-indigo-500", label: "Supplier" },
    settings: { icon: Gear, color: "bg-gray-50 dark:bg-slate-8000", label: "Settings" },
    system: { icon: Gear, color: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400", label: "System" },
    general: { icon: ClockCounterClockwise, color: "bg-gray-400", label: "General" },
  };
  return configs[type] || configs.general;
};

export default function ActivitiesPage() {
  const dispatch = useDispatch();

  const { activities, pagination, types, isLoading } = useSelector((state) => state.activity);
  const { users } = useSelector((state) => state.users);

  // Filter state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  // Default to today's data
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch activities when filters change
  useEffect(() => {
    dispatch(getActivities({
      page,
      limit,
      search: debouncedSearch,
      type: selectedType === "all" ? "" : selectedType,
      userId: selectedUser === "all" ? "" : selectedUser,
      startDate,
      endDate,
    }));
  }, [dispatch, page, limit, debouncedSearch, selectedType, selectedUser, startDate, endDate]);

  // Fetch activity types and users on mount
  useEffect(() => {
    dispatch(getActivityTypes());
    dispatch(fetchUsers());
  }, [dispatch]);

  // Quick date range presets
  const setDateRange = useCallback((preset) => {
    const today = new Date();
    let start = new Date();

    switch (preset) {
      case "today":
        start = today;
        break;
      case "week":
        start.setDate(today.getDate() - 7);
        break;
      case "month":
        start.setMonth(today.getMonth() - 1);
        break;
      case "all":
        setStartDate("");
        setEndDate("");
        setPage(1);
        return;
      default:
        return;
    }

    setStartDate(format(start, "yyyy-MM-dd"));
    setEndDate(format(today, "yyyy-MM-dd"));
    setPage(1);
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearch("");
    setSelectedType("all");
    setSelectedUser("all");
    setStartDate("");
    setEndDate("");
    setPage(1);
  }, []);

  // Format activity timestamp
  const formatTime = useCallback((timestamp) => {
    return format(new Date(timestamp), "MMM d, yyyy 'at' h:mm a");
  }, []);

  return (
    <div className="mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ClockCounterClockwise className="h-8 w-8" />
            Activity Log
          </h1>
          <p className="text-muted-foreground mt-1">
            View all system activities and audit trail
          </p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {pagination?.total || 0} Total Activities
        </Badge>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FunnelSimple className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search activities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={selectedType} onValueChange={(val) => { setSelectedType(val); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {types.map((type) => (
                  <SelectItem key={type} value={type}>
                    {getActivityTypeConfig(type).label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedUser} onValueChange={(val) => { setSelectedUser(val); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.first_name || user.username || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                className="flex-1"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                className="flex-1"
              />
            </div>
          </div>

          {/* Quick date presets */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setDateRange("today")}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDateRange("week")}>
              Last 7 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDateRange("month")}>
              Last 30 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDateRange("all")}>
              All Time
            </Button>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activities Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : activities.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <ClockCounterClockwise className="h-12 w-12 mb-4" />
            <p className="text-lg">No activities found</p>
            <p className="text-sm">Try adjusting your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="enterprise-table-container">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>User</TableHead>
                <TableHead className="w-[200px]">Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.map((activity) => {
                const config = getActivityTypeConfig(activity.type);
                const Icon = config.icon;

                return (
                  <TableRow key={activity.id}>
                    <TableCell>
                      <Badge variant="secondary" className={`${config.color} text-white`}>
                        <Icon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {activity.description}
                    </TableCell>
                    <TableCell>
                      {activity.user ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs">
                            {activity.user.first_name?.[0] || activity.user.username?.[0] || "?"}
                          </div>
                          <span className="text-sm">
                            {activity.user.first_name || activity.user.username || "Unknown"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">System</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatTime(activity.createdAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select
              value={limit.toString()}
              onValueChange={(value) => {
                setLimit(parseInt(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder={limit} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1 || isLoading}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm font-medium">
              Page {pagination.page} of {pagination.pages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(pagination.pages, page + 1))}
              disabled={page >= pagination.pages || isLoading}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Info Card */}
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Activity logs are immutable and cannot be edited or deleted.
            All user actions in the system are automatically recorded for audit purposes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
